import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { courseService } from '../../services/courseService';
import { publicCourse, COURSE } from '../../data/courseData';
import { COURSE_PRODUCT } from '../../data/courseProduct';
import { addToCart } from '../../redux/slices/cartSlice';
import { selectIsAuthenticated } from '../../redux/slices/authSlice';
import { resolveImageUrl } from '../../utils/imageUrl';
import {
  FiBookOpen,
  FiZap,
  FiCreditCard,
  FiUnlock,
  FiRefreshCw,
  FiShoppingCart,
  FiPlay,
  FiLock,
  FiGift,
  FiCheckCircle,
  FiCheck,
} from 'react-icons/fi';
import {
  PageContainer,
  Wrapper,
  Header,
  ProgressLine,
  CtaBar,
  SectionLabel,
  ModuleCard,
  ModuleHeader,
  LessonList,
  LessonRow,
  PricingCard,
  UnlockBanner,
  FreeBadge,
} from './Curso.styles';

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function formatPriceBRL(cents) {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatDateBR(iso) {
  // 'YYYY-MM-DD' -> 'DD/MM/YYYY'
  if (!iso || typeof iso !== 'string') return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

const Curso = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);

  // Aviso vindo do checkout após comprar o combo (curso + eBooks): mostra um
  // banner com link para "Minhas Compras". `ebooksLiberados` = qtd de eBooks.
  const ebooksLiberados = location.state?.ebooksLiberados || 0;
  const [showEbookNotice, setShowEbookNotice] = useState(ebooksLiberados > 0);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const cartItems = useSelector((state) => state.cart?.items || []);

  // O conteúdo do curso é 100% local (publicCourse, sem rede), então já fica
  // disponível no primeiro render — sem spinner nem "lista branca".
  const [course] = useState(() => publicCourse());
  const [enrolled, setEnrolled] = useState(false);
  const [completedIds, setCompletedIds] = useState(new Set());
  const [openModuleId, setOpenModuleId] = useState(() => course.modules?.[0]?.id ?? null);
  const [isNavigating, setIsNavigating] = useState(false);
  // Aviso: comprar o curso exige conta. Em vez de ir direto pro login,
  // mostramos um popup explicando (igual ao checkout).
  const [showAuthNotice, setShowAuthNotice] = useState(false);

  // Reseta scroll ao entrar na página
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Busca matrícula + aulas concluídas em segundo plano. Não bloqueia a tela:
  // o curso já renderizou com os dados locais; estes campos só refinam o estado
  // (enrolled/concluídas) quando chegam.
  useEffect(() => {
    let cancelled = false;
    courseService
      .getAccess(token)
      .then((accessData) => {
        if (cancelled || !accessData) return;
        setEnrolled(Boolean(accessData.enrolled));
        setCompletedIds(new Set(accessData.completedLessonIds || []));
      })
      .catch((err) => {
        // Sem acesso (não autenticado / API indisponível): segue como não-matriculado.
        console.debug('Sem dados de acesso:', err?.message);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Refetch quando o usuário retorna à aba (ex: voltou da página de aula em outra aba)
  useEffect(() => {
    let cancelled = false;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible' || !token) return;
      try {
        const accessData = await courseService.getAccess(token);
        if (!cancelled && accessData) {
          setCompletedIds(new Set(accessData.completedLessonIds || []));
          setEnrolled(Boolean(accessData.enrolled));
        }
      } catch (err) {
        console.debug('Erro refetchando acesso:', err?.message);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token]);

  const totals = useMemo(() => {
    if (!course || !Array.isArray(course.modules)) return { total: 0, completed: 0 };
    const total = course.modules.reduce((sum, m) => sum + (Array.isArray(m?.lessons) ? m.lessons.length : 0), 0);
    let completed = 0;
    course.modules.forEach((m) => {
      if (m && Array.isArray(m.lessons)) {
        m.lessons.forEach((l) => {
          if (l && completedIds.has(l.id)) completed += 1;
        });
      }
    });
    return { total, completed };
  }, [course, completedIds]);

  const firstUncompletedLessonId = useMemo(() => {
    if (!course || !Array.isArray(course.modules)) return null;
    for (const m of course.modules) {
      if (!m || !Array.isArray(m.lessons)) continue;
      for (const l of m.lessons) {
        if (l && !completedIds.has(l.id)) return l.id;
      }
    }
    return course.modules[0]?.lessons?.[0]?.id ?? null;
  }, [course, completedIds]);

  // Handler para abrir/fechar módulos (memoizado)
  const handleToggleModule = React.useCallback((moduleId) => {
    setOpenModuleId(prev => prev === moduleId ? null : moduleId);
  }, []);

  // Handler para clicar em aula (memoizado)
  const createLessonClickHandler = React.useCallback((lessonId, enrolled, isFreeModule) => {
    return () => {
      if (!isAuthenticated) {
        navigate('/login', { state: { from: '/curso-de-fingerstyle' } });
        return;
      }
      if (enrolled || isFreeModule) {
        setIsNavigating(true);
        navigate(`/curso-de-fingerstyle/aula/${lessonId}`);
      }
    };
  }, [isAuthenticated, navigate]);
  // 'enrolled' é passado como parâmetro para o factory e não é uma dependência do closure
  // portanto removemos para evitar warning do ESLint.

  const alreadyInCart = cartItems.some((item) => item.id === COURSE_PRODUCT.id);

  const handleBuy = React.useCallback(() => {
    if (!alreadyInCart) {
      dispatch(
        addToCart({
          ...COURSE_PRODUCT,
          price: course.priceCents,
          originalPrice: course.originalPriceCents,
        }),
      );
    }
    setIsNavigating(true);
    navigate('/checkout');
  }, [alreadyInCart, course?.priceCents, course?.originalPriceCents, dispatch, navigate]);

  const handleWatch = React.useCallback(() => {
    if (firstUncompletedLessonId) {
      setIsNavigating(true);
      navigate(`/curso-de-fingerstyle/aula/${firstUncompletedLessonId}`);
    }
  }, [firstUncompletedLessonId, navigate]);

  return (
    <PageContainer>
      <Wrapper>
        <div style={{
          opacity: isNavigating ? 0.4 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isNavigating ? 'none' : 'auto',
        }}>
        <Header>
          {course.image && <img src={course.image} alt="" />}
          <h1>{course.title || ''}</h1>
          <p>{course.description || ''}</p>
        </Header>

        {showAuthNotice && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.72)',
              backdropFilter: 'blur(3px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: 16,
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 440,
                background: '#141414',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: 14,
                padding: '28px 24px',
                textAlign: 'center',
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.55)',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  margin: '0 auto 16px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 215, 0, 0.1)',
                  border: '2px solid #ffd700',
                  color: '#ffd700',
                  fontSize: 32,
                }}
              >
                <FiLock />
              </div>
              <h2 style={{ color: '#fff', fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>
                Crie sua conta para levar o curso
              </h2>
              <p style={{ color: '#cfcfcf', fontSize: 14, lineHeight: 1.5, margin: '0 0 22px' }}>
                O acesso ao curso fica liberado na sua conta, então você precisa entrar ou criar
                uma conta antes de comprar.
              </p>
              <button
                type="button"
                onClick={() => navigate('/cadastro', { state: { from: '/curso-de-fingerstyle' } })}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#ffd700',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 800,
                  fontSize: 15,
                  cursor: 'pointer',
                  marginBottom: 10,
                }}
              >
                Criar conta
              </button>
              <button
                type="button"
                onClick={() => navigate('/login', { state: { from: '/curso-de-fingerstyle' } })}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  color: '#ffd700',
                  border: '1px solid rgba(255, 215, 0, 0.5)',
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer',
                  marginBottom: 10,
                }}
              >
                Já tenho conta — Entrar
              </button>
              <button
                type="button"
                onClick={() => setShowAuthNotice(false)}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'transparent',
                  color: '#9aa0a6',
                  border: 'none',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Voltar
              </button>
            </div>
          </div>
        )}

        {showEbookNotice && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 12,
              margin: '0 0 24px',
              padding: '14px 18px',
              borderRadius: 10,
              border: '1px solid #ffd700',
              backgroundColor: 'rgba(255, 215, 0, 0.08)',
            }}
          >
            <span style={{ flex: '1 1 260px', color: '#f5f5f5', fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <FiCheckCircle style={{ color: '#ffd700', flexShrink: 0 }} />
              <span>
                {ebooksLiberados > 1 ? 'Seus eBooks foram liberados' : 'Seu eBook foi liberado'}!
                {' '}Você encontra {ebooksLiberados > 1 ? 'eles' : 'ele'} na página “Minhas Compras”.
              </span>
            </span>
            <Link
              to="/minhas-compras"
              style={{
                display: 'inline-block',
                padding: '10px 18px',
                backgroundColor: '#ffd700',
                color: '#1a1a1a',
                borderRadius: 6,
                fontWeight: 700,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Ver minhas compras →
            </Link>
            <button
              type="button"
              aria-label="Fechar aviso"
              onClick={() => setShowEbookNotice(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#f5f5f5',
                fontSize: 20,
                lineHeight: 1,
                cursor: 'pointer',
                padding: '0 4px',
              }}
            >
              ×
            </button>
          </div>
        )}

        {isNavigating && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}>
            <div style={{
              textAlign: 'center',
              animation: 'fadeIn 0.3s ease-in',
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                margin: '0 auto 20px',
                border: '4px solid rgba(255, 215, 0, 0.25)',
                borderTopColor: '#ffd700',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
              <p style={{
                color: '#fff',
                fontSize: '16px',
                margin: 0,
                fontWeight: '500',
              }}>
                Carregando aula…
              </p>
            </div>
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        <ProgressLine>
          <div className="left">
            <div className="icon"><FiBookOpen style={{ color: '#ffd700' }} /></div>
            <div className="text">
              <strong>{totals.completed}</strong> / {totals.total} conteúdos
            </div>
          </div>
          <div className="percentage">
            {totals.total ? Math.round((totals.completed / totals.total) * 100) : 0}%
          </div>
        </ProgressLine>

        {!enrolled && course?.priceCents !== undefined && (
          <PricingCard>
            <span className="badge">
              <FiZap style={{ verticalAlign: '-1px', marginRight: 4 }} />
              {course.priceCents === COURSE.priceSchedule[0].priceCents
                ? 'VALOR ESPECIAL DE LANÇAMENTO'
                : 'OFERTA POR TEMPO LIMITADO'}
            </span>
            <div className="hoje">
              {/* Headline no valor do PIX (R$ 147,00). */}
              Hoje: <strong>{formatPriceBRL(14700)}</strong> <span style={{ fontSize: '0.7em', fontWeight: 600 }}>no PIX</span>
            </div>
            <div className="de">
              De: <s>{formatPriceBRL(course.originalPriceCents || 0)}</s>
            </div>
            <div className="benefits">
              <div className="benefit-item">
                <span className="benefit-icon"><FiCreditCard style={{ color: '#ffd700' }} /></span>
                <div className="benefit-text">
                  <span className="benefit-label">Pagamento único</span>
                  <span className="benefit-desc">Sem mensalidades</span>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon"><FiUnlock style={{ color: '#ffd700' }} /></span>
                <div className="benefit-text">
                  <span className="benefit-label">Acesso vitalício</span>
                  <span className="benefit-desc">Seu para sempre</span>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon"><FiRefreshCw style={{ color: '#ffd700' }} /></span>
                <div className="benefit-text">
                  <span className="benefit-label">Atualizações incluídas</span>
                  <span className="benefit-desc">Novos conteúdos sem custo</span>
                </div>
              </div>
            </div>
            {Array.isArray(course.upcomingTiers) && course.upcomingTiers.length > 0 && (
              <div className="upcoming">
                <div className="upcoming-title">Próximos reajustes:</div>
                <ul>
                  {course.upcomingTiers.map((tier) => (
                    <li key={tier?.effectiveFrom || Math.random()}>
                      <span className="date">{formatDateBR(tier?.effectiveFrom)}</span>
                      <span className="arrow">→</span>
                      <span className="price">{formatPriceBRL(tier?.priceCents || 0)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </PricingCard>
        )}

        <CtaBar>
          {enrolled ? (
            <button onClick={handleWatch} disabled={isNavigating}><FiPlay /> Assistir Aula</button>
          ) : isAuthenticated ? (
            <button onClick={handleBuy} disabled={isNavigating}>
              <FiShoppingCart /> {alreadyInCart ? 'Ir para o carrinho' : 'Comprar Curso Agora'}
            </button>
          ) : (
            <button onClick={() => setShowAuthNotice(true)}>
              <FiShoppingCart /> Comprar Curso Agora
            </button>
          )}
        </CtaBar>

        {!enrolled && (
          <p style={{
            textAlign: 'center',
            fontSize: '11px',
            color: 'rgba(255,255,255,0.4)',
            margin: '-8px 0 20px',
            lineHeight: '1.5',
          }}>
            Ao concluir, você declara que leu e está de acordo com os{' '}
            <a
              href="/termo-de-uso-e-politica-de-privacidade"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgba(255,215,0,0.6)', textDecoration: 'underline' }}
            >
              termos de uso e política de privacidade
            </a>
            .
          </p>
        )}

        <SectionLabel>Lista de Conteúdos</SectionLabel>

        {Array.isArray(course?.modules) && course.modules.map((module, moduleIdx) => {
          if (!module || !module.lessons) return null;
          const moduleCompleted = module.lessons.filter((l) => l && completedIds.has(l.id)).length;
          const open = openModuleId === module.id;
          const isFreeModule = module.freeAccess === true;
          const freeLessonsCount = !isFreeModule ? module.lessons.filter((l) => l.freeAccess === true).length : 0;
          const isFirstPaidModule =
            !isFreeModule &&
            course.modules.slice(0, moduleIdx).every((m) => m?.freeAccess === true);
          return (
            <React.Fragment key={module.id}>
              {/* CTA "Liberar curso completo" entre os módulos grátis e os pagos */}
              {isFirstPaidModule && !enrolled && (
                <UnlockBanner>
                  <div className="text">
                    <strong><FiLock style={{ verticalAlign: '-2px', marginRight: 6 }} />Conteúdo bloqueado</strong>
                  </div>
                  {isAuthenticated ? (
                    <button onClick={handleBuy} disabled={isNavigating}>
                      {alreadyInCart ? 'Ir para o carrinho' : 'LIBERAR CURSO COMPLETO'}
                    </button>
                  ) : (
                    <button onClick={() => setShowAuthNotice(true)}>
                      LIBERAR CURSO COMPLETO
                    </button>
                  )}
                </UnlockBanner>
              )}

              <ModuleCard $isPaid={!isFreeModule && !enrolled}>
                <ModuleHeader
                  $open={open}
                  $isPaid={!isFreeModule && !enrolled}
                  onClick={() => handleToggleModule(module.id)}
                  aria-expanded={open}
                >
                  <div>
                    <div className="title">
                      {module.title}
                      {isFreeModule && <FreeBadge>GRÁTIS</FreeBadge>}
                    </div>
                    <div className="meta">
                      {enrolled || isFreeModule
                        ? `${moduleCompleted} / ${module.lessons.length} aulas concluídas`
                        : `${module.lessons.length} aulas`}
                    </div>
                    {!isFreeModule && !enrolled && (
                      <div className="locked-badge">
                        <FiLock /> Disponível apenas para alunos
                      </div>
                    )}
                    {!isFreeModule && !enrolled && freeLessonsCount > 0 && (
                      <div className="free-lesson-hint">
                        <FiGift /> {freeLessonsCount} aula grátis disponível
                      </div>
                    )}
                  </div>
                  <span className="chev" aria-hidden="true">▾</span>
                </ModuleHeader>

                {open && (
                  <LessonList>
                    {Array.isArray(module.lessons) && module.lessons.map((lesson, idx) => {
                      if (!lesson || !lesson.id) return null;
                      const isDone = completedIds.has(lesson.id);
                      const today = new Date().toISOString().slice(0, 10);
                      const isLocked = lesson.releaseDate && lesson.releaseDate > today;
                      const isFreeLesson = lesson.freeAccess === true;
                      const canPlay = isAuthenticated && (enrolled || isFreeModule || isFreeLesson) && !isLocked;
                      const handleLessonClick = createLessonClickHandler(lesson.id, enrolled, isFreeModule || isFreeLesson);

                      return (
                        <LessonRow
                          key={lesson.id}
                          $clickable={canPlay}
                          $dimmed={!enrolled && !isFreeModule && !isFreeLesson}
                          onClick={handleLessonClick}
                        >
                          <div className="thumb">
                            {lesson.image && (
                              <img
                                src={resolveImageUrl(lesson.image)}
                                alt={lesson.title}
                                loading="lazy"
                              />
                            )}
                            <span className="duration">{formatDuration(lesson.durationSeconds)}</span>
                          </div>
                          <div className="info">
                            <span className="index">{idx + 1})</span>
                            <span className="title">{lesson.title}</span>
                            {isFreeLesson && !isFreeModule && <FreeBadge>GRÁTIS</FreeBadge>}
                            {isLocked && (
                              <span className="release">
                                <FiLock style={{ verticalAlign: '-2px', marginRight: 4 }} />
                                Disponível em {formatDateBR(lesson.releaseDate)}
                              </span>
                            )}
                          </div>
                          {isDone && <span className="done" title="Concluída"><FiCheck /></span>}
                        </LessonRow>
                      );
                    })}
                  </LessonList>
                )}
              </ModuleCard>
            </React.Fragment>
          );
        })}
        </div>
      </Wrapper>
      <style>{`
        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </PageContainer>
  );
};

export default Curso;
